import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Target, ArrowLeft, TrendingUp, Users, Trophy, ChevronDown, ChevronRight,
  CheckCircle2, Clock, Calendar, Loader2, RefreshCw, ExternalLink, FileDown,
} from "lucide-react";
import api from "@/lib/api";

const METRIC_LABEL = {
  contract_value: "Valor do contrato",
  deposit_total_paid: "Caução paga",
  contracts_count: "Contratos fechados",
  monthly_value_annualised: "Valor do contrato",
};

const METRIC_UNIT = {
  contract_value: "€",
  deposit_total_paid: "€",
  contracts_count: "contratos",
  monthly_value_annualised: "€",
};

function fmtEUR(v) {
  return new Intl.NumberFormat("pt-PT", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(Number(v || 0));
}

function fmtNumber(v) {
  return new Intl.NumberFormat("pt-PT").format(Number(v || 0));
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("pt-PT");
}

function fmtValue(value, metric) {
  if (metric === "contracts_count") return fmtNumber(value);
  return fmtEUR(value);
}

// ---- Progress Bar ----------------------------------------------------------
function ProgressBar({ pct, met }) {
  const width = Math.min(100, Math.max(0, pct));
  return (
    <div className="w-full">
      <div className="h-3 bg-muted/40 rounded-full overflow-hidden border border-border">
        <div
          className={`h-full transition-all duration-700 rounded-full ${
            met ? "bg-emerald-500" : pct >= 75 ? "bg-primary" : "bg-foreground"
          }`}
          style={{ width: `${width}%` }}
          data-testid="progress-bar-fill"
        />
      </div>
      <div className="flex justify-between mt-1.5 text-[11px] text-[#6B7280]">
        <span>0%</span>
        <span className={met ? "font-semibold text-[hsl(var(--success))]" : "font-semibold text-foreground"}>{pct.toFixed(1)}%</span>
        <span>100%</span>
      </div>
    </div>
  );
}

// ---- Operator Row (expandable to show leads) --------------------------------
function OperatorRow({ report, index, metric, targetMet }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-border rounded-xl bg-card" data-testid={`operator-row-${report.operator_id}`}>
      <div className="flex items-center gap-4 px-5 py-4">
        <button
          onClick={() => setExpanded(e => !e)}
          className="text-[#6B7280] hover:text-foreground"
          data-testid={`operator-toggle-${report.operator_id}`}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
          index === 0 ? "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))]" :
          index === 1 ? "bg-muted text-foreground" :
          index === 2 ? "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))]" :
          "bg-muted/40 text-[#6B7280]"
        }`}>
          #{index + 1}
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate" data-testid={`operator-name-${report.operator_id}`}>
            {report.operator_name}
          </div>
          {report.operator_email && (
            <div className="text-xs text-[#6B7280] truncate">{report.operator_email}</div>
          )}
        </div>

        <div className="hidden sm:flex flex-col items-end min-w-[100px]">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Convertidos</div>
          <div className="text-sm font-semibold text-foreground" data-testid={`operator-converted-${report.operator_id}`}>
            {report.converted_count}
          </div>
        </div>

        <div className="flex flex-col items-end min-w-[110px]">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            {METRIC_LABEL[metric]}
          </div>
          <div className="text-sm font-semibold text-foreground" data-testid={`operator-value-${report.operator_id}`}>
            {fmtValue(report.total_value, metric)}
          </div>
        </div>

        <div className="flex flex-col items-end min-w-[110px]">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Bónus</div>
          <div className={`text-base font-bold ${targetMet ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`} data-testid={`operator-bonus-${report.operator_id}`}>
            {fmtEUR(report.bonus_amount)}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border bg-muted/40 px-5 py-3">
          {report.leads.length === 0 ? (
            <div className="text-xs text-[#6B7280] py-2">Sem leads convertidos.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <th className="text-left py-2 font-semibold">Cliente</th>
                  <th className="text-left py-2 font-semibold">Beneficiário</th>
                  <th className="text-left py-2 font-semibold">Contrato</th>
                  <th className="text-left py-2 font-semibold">Caução paga</th>
                  <th className="text-right py-2 font-semibold">Valor</th>
                  <th className="text-right py-2 font-semibold">Lead</th>
                </tr>
              </thead>
              <tbody className="text-foreground">
                {report.leads.map(l => (
                  <tr key={l.lead_id} className="border-t border-border" data-testid={`lead-row-${l.lead_id}`}>
                    <td className="py-1.5">{l.contact_name}</td>
                    <td className="py-1.5">{l.beneficiary_name || "—"}</td>
                    <td className="py-1.5 text-[#6B7280]">{fmtDate(l.signed_at)}</td>
                    <td className="py-1.5 text-[#6B7280]">{fmtDate(l.deposit_paid_at)}</td>
                    <td className="py-1.5 text-right font-semibold">{fmtValue(l.value, metric)}</td>
                    <td className="py-1.5 text-right">
                      <Link
                        to={`/leads/${l.lead_id}`}
                        className="inline-flex items-center gap-1 text-foreground hover:underline text-[11px]"
                        data-testid={`lead-open-${l.lead_id}`}
                      >
                        Abrir <ExternalLink className="w-2.5 h-2.5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ---- PAGE ------------------------------------------------------------------
export default function ObjectiveBonusDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get(`/objectives/${id}/bonuses`);
      setReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a carregar dashboard");
      if (e.response?.status === 404) navigate("/settings/offices");
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, [id]);  // eslint-disable-line

  if (loading) {
    return (
      <div className="p-8 lg:p-12 max-w-6xl mx-auto flex items-center gap-2 text-sm text-[#6B7280]">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar dashboard…
      </div>
    );
  }

  if (!report) return null;

  const { objective, achievement, operators, total_bonus_pool } = report;
  const targetMet = achievement.target_met;
  const totalConverted = achievement.total_converted_leads;

  return (
    <div className="p-8 lg:p-12 max-w-6xl mx-auto space-y-6" data-testid="objective-dashboard-root">
      {/* Header ---------------------------------------------------------- */}
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <Link
            to="/settings/offices"
            className="inline-flex items-center gap-1 text-xs text-[#6B7280] hover:text-foreground mb-2"
            data-testid="back-to-offices"
          >
            <ArrowLeft className="w-3 h-3" /> Escritórios & Objectivos
          </Link>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-1">
            {objective.office_name} · Objectivo
          </div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight" data-testid="objective-title">
            {objective.name}
          </h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-[#6B7280] flex-wrap">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(objective.start_date)} → {fmtDate(objective.end_date)}</span>
            <span>·</span>
            <span>Métrica: <strong className="text-foreground">{METRIC_LABEL[objective.target_metric]}</strong></span>
            <span>·</span>
            <span>Bónus: <strong className="text-foreground">{objective.bonus_percentage}%</strong></span>
            <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full font-semibold ${
              objective.status === "active" ? "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]" : "bg-muted/40 text-[#6B7280]"
            }`}>{objective.status === "active" ? "Activa" : "Fechada"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={async () => {
              try {
                const resp = await api.get(`/objectives/${id}/bonuses.pdf`,
                  { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `Bonus-${objective.name?.replace(/[^a-z0-9-]/gi, "_") || "objectivo"}.pdf`;
                document.body.appendChild(a); a.click();
                setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
              } catch (e) {
                toast.error("Falha ao gerar PDF");
              }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-[#1E293B] text-primary-foreground hover:bg-[#0F1E2A]"
            data-testid="obj-pdf-btn"
          >
            <FileDown className="w-4 h-4" /> PDF
          </button>
          <button
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-border hover:bg-muted/40 disabled:opacity-50"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Actualizar
          </button>
        </div>
      </div>

      {/* Achievement card ----------------------------------------------- */}
      <div className={`rounded-2xl border-2 p-8 ${
        targetMet ? "border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))]/60" : "border-border bg-card"
      }`} data-testid="achievement-card">
        <div className="flex items-start justify-between gap-6 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {targetMet ? (
                <CheckCircle2 className="w-6 h-6 text-[hsl(var(--success))]" />
              ) : (
                <Clock className="w-6 h-6 text-primary" />
              )}
              <span className={`text-[11px] uppercase tracking-[0.2em] font-bold ${
                targetMet ? "text-[hsl(var(--success))]" : "text-primary"
              }`} data-testid="status-badge">
                {targetMet ? "Meta atingida" : "Em progresso"}
              </span>
            </div>
            <div className="font-display text-3xl lg:text-4xl tracking-tight text-foreground" data-testid="achieved-value">
              {fmtValue(achievement.achieved_value, achievement.target_metric)}
              <span className="text-lg text-[#6B7280] font-body font-normal ml-2">
                / {fmtValue(achievement.target_value, achievement.target_metric)}
              </span>
            </div>
            <div className="text-xs text-[#6B7280] mt-1">
              Valor {targetMet ? "atingido" : "acumulado"} · {METRIC_UNIT[achievement.target_metric]}
            </div>
          </div>
        </div>
        <ProgressBar pct={achievement.achievement_percentage} met={targetMet} />
      </div>

      {/* KPIs ------------------------------------------------------------ */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-card p-5" data-testid="kpi-converted">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-foreground" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-semibold">Leads convertidos</span>
          </div>
          <div className="font-display text-3xl text-foreground">{totalConverted}</div>
          <div className="text-xs text-[#6B7280] mt-1">Contract validated + caução paga</div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5" data-testid="kpi-operators">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-foreground" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-semibold">Operadores</span>
          </div>
          <div className="font-display text-3xl text-foreground">{operators.length}</div>
          <div className="text-xs text-[#6B7280] mt-1">Atendimento com leads convertidos</div>
        </div>

        <div className={`rounded-xl border p-5 ${
          targetMet ? "border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))]/50" : "border-border bg-card"
        }`} data-testid="kpi-bonus-pool">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className={`w-4 h-4 ${targetMet ? "text-[hsl(var(--success))]" : "text-primary"}`} />
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-semibold">Pool de bónus</span>
          </div>
          <div className={`font-display text-3xl ${targetMet ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`}>
            {fmtEUR(total_bonus_pool)}
          </div>
          <div className="text-xs text-[#6B7280] mt-1">
            {targetMet ? `Distribuído por ${operators.length} operador(es)` : "Só distribuído se atingir meta"}
          </div>
        </div>
      </div>

      {/* Operators ranking --------------------------------------------- */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl">Ranking de operadores</h2>
          <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
            Ordenados por valor trazido
          </div>
        </div>

        {operators.length === 0 ? (
          <div className="border-2 border-dashed border-border rounded-xl p-10 text-center bg-card" data-testid="operators-empty">
            <Target className="w-10 h-10 mx-auto text-primary mb-3" />
            <div className="font-display text-lg mb-1">Ainda sem conversões</div>
            <p className="text-sm text-[#6B7280]">
              Nenhum lead convertido dentro deste período — o dashboard actualiza automaticamente à medida que os contratos fecharem.
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="operators-list">
            {operators.map((op, i) => (
              <OperatorRow
                key={op.operator_id}
                report={op}
                index={i}
                metric={objective.target_metric}
                targetMet={targetMet}
              />
            ))}
          </div>
        )}
      </div>

      {objective.notes && (
        <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm text-[#6B7280]">
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold mb-1 text-muted-foreground">Notas do objectivo</div>
          {objective.notes}
        </div>
      )}
    </div>
  );
}
